import { browser, CollectionView, util } from '@glu/core';

import HeaderView from '../column/columnHeader';
import comparatorFactory from '../comparatorFactory';

export default CollectionView.extend({
  itemView: HeaderView,
  tagName: 'tr',

  collectionEvents: {
    // TODO: don't know why we need re-render when we are changing something in the column.
    // 'change': 'render',
    'change:condition reorder': 'handleColumnsChange'
  },

  itemViewEventPrefix: 'column:header',

  itemViewOptions() {
    return {
      sortable: this.sortable,
      filterable: this.filterable,
      resizable: this.resizable,
      draggable: this.draggable,
      defaultWidth: this.defaultColumnWidth,
      masterCollection: this.options.masterCollection,
      disableDynamicColumnWidths: this.options.disableDynamicColumnWidths,
      gridCid: this.gridCid,
      grid: this.grid
    };
  },

  initialize(options) {
    this.sortableCollection = options.sortableCollection;
    this.clientSidePagination = options.clientSidePagination;
    this.layout = options.layout;
    this.sortMethod = options.sortMethod;
    this.sortable = options.sortable;
    this.filterable = options.filterable;
    this.resizable = options.resizable;
    this.draggable = options.draggable;
    this.stateModel = options.stateModel;
    this.defaultColumnWidth = options.defaultColumnWidth;
    this.gridCid = options.gridCid;
    this.grid = options.grid;
    this.enableColumnRename = options.enableColumnRename;

    this.initDefaultSort(options);
  },


  delegateEvents(...args) {
    CollectionView.prototype.delegateEvents.apply(this, args);

    this.listenTo(this, {
      'column:header:click': this.handleItemViewClick
    });
  },

  handleColumnsChange() {
    // this view will be re-rendered for IE in the tableLayout class
    if (browser.msie) {
      return false;
    }

    this.render();
  },

  /**
   * Initializes any default sort on the grid if a default sort is setup.
   * Defaults to ascending sort order on the specified column if nothing is specified.
   *
   * @param opts The grid options.
   */
  initDefaultSort(opts) {
    const { defaultSort } = opts;
    let sortCol;
    let sortOrder;

    // need to clear previous sort.
    this.collection.each(this.clearSortedModel, this);
    if (defaultSort && defaultSort.field) {
      sortCol = this.collection.findWhere({
        field: defaultSort.field
      });
      if (sortCol) {
        sortOrder = defaultSort.order === 'desc' ? 'asc' : 'desc';
        sortCol.set({
          sort: true, // turn on sorting for this column
          sorted: sortOrder // default to ascending sort if none supplied.  Set the opposite of what we want as applySimpleFieldSort reverses it
        });

        this.collection.each(this.applySimpleFieldSort, this);
      }
    }
  },

  handleReorder() {
    if (this.stateModel !== null) {
      this.stateModel.setColumnOrder(this.collection.toJSON());
    }

    this.trigger('reorder', this);
    this.collection.trigger('reorder');

    this.sortableCollection.trigger('change', this.sortableCollection);
  },

  handleItemViewClick(event, view) {
    // If sorting is disabled at the grid-level, return early
    if (!this.options.sortable) {
      return;
    }

    // If sorting is disabled for this particular column, return early
    if (view.model.has('sortable') && view.model.get('sortable') === false) {
      return;
    }

    this.collection.each(this.clearSortedModel, this);

    view.model.set('sort', true, {
      silent: true
    });

    this.applySimpleFieldSort(view.model);

    if (this.clientSidePagination) {
      this.layout.triggerMethod('tableView:clientSidePagination:sort');
    }

    this.render();
  },

  clearSortedModel(model) {
    model.unset('sort', {
      silent: true
    });
  },

  applySimpleFieldSort(columnDef) {
    if (columnDef.get('sort')) {
      let comparator = columnDef.get('field');
      const sorted = columnDef.get('sorted');

      if (util.isUndefined(sorted) || sorted === 'desc') {
        columnDef.set('sorted', 'asc');
        comparator = comparatorFactory.ascendingByField(columnDef.get('field'));
      } else {
        columnDef.set('sorted', 'desc');
        comparator = comparatorFactory.descendingByField(columnDef.get('field'));
      }

      if (this.stateModel !== null) {
        const sort = {};

        sort[columnDef.get('field')] = columnDef.get('sorted');

        this.stateModel.set('sort', sort);
      }

      if (typeof this.sortMethod === 'function') {
        return this.sortMethod(columnDef.get('field'), columnDef.get('sorted'));
      }

      this.sortableCollection.comparator = comparator;

      this.sortableCollection.each((row, index) => {
        row.gluStableSortIndex = index;
      });
      this.sortableCollection.sort({
        comparator
      });

      return true;
    }
  },

  getItemView(item) {
    return item.get('headerView') || this.itemView;
  },

  addItemView(item, ItemView, index) {
    if (item.has('condition')) {
      const isFunction = util.isFunction(item.get('condition')) && item.get('condition')(item) === false;
      const isBoolean = util.isBoolean(item.get('condition')) && !item.get('condition');
      if (isFunction || isBoolean) {
        return false;
      }
    }
    return CollectionView.prototype.addItemView.call(this, item, ItemView, index);
  },

  onRender() {
    if (this.draggable) {
      this.initReorder();
    }

    this.addManageColumnsOffset();
  },

  // will add offset to last column with manageColumns width.
  addManageColumnsOffset() {
    if (this.options.disableColumnManagement) {
      return;
    }

    const view = this.children.last();

    if (view && view.addManageColumnsOffset) {
      view.addManageColumnsOffset();
    }
  },

  initReorder() {
    const draggableOptions = {
      // items to skip
      // note: have to remove span from "cancel", otherwise drag event doesn't work for icons
      cancel: 'a:not(.draggable), .grid-row-selector, .grid-column-resize-marker',
      axis: 'x',
      helper: 'clone',
      revert: true,
      cursor: 'move',
      placeholder: 'grid-column-order-placeholder',
      // items to handle
      handle: '.draggable',
      start: this.handleDragStart.bind(this),
      update: this.handleStop.bind(this)
    };

    // need to destroy previous reorder
    this.destroyReorder();

    this.order = null;

    this.orderActiveClassName = 'grid-column-order-active';

    this.$el.sortable(draggableOptions).disableSelection();
  },

  handleDragStart(e, ui) {
    const $th = ui.item;
    const $helper = ui.helper;
    const sortableInstance = this.$el.sortable('instance');
    const $placeholder = sortableInstance.placeholder;

    if (this.isClosed || !$th.length) {
      return;
    }

    $helper.addClass('dragging');

    // set placeholder and helper width same as ordered element
    $helper.width($th.width());
    $placeholder.width($th.width());

    this.$el.addClass(this.orderActiveClassName);

    this.order = $th.index();
  },

  handleStop(e, ui) {
    const $th = ui.item;

    if (this.isClosed || !$th.length) {
      return;
    }

    this.$el.removeClass(this.orderActiveClassName);

    const newOrder = $th.index();
    const columnCid = $th.attr('data-cid');
    const column = this.collection.get(columnCid);
    const newOrderAdjusted = this.adjustedIndex(newOrder);

    if (newOrder > -1 && this.order !== newOrder) {
      this.collection.remove(column, {
        silent: true
      });
      this.collection.add(column, {
        at: newOrderAdjusted,
        // no need to re-render grid now it will be re-rendered after reorder event
        silent: true
      });
      this.handleReorder();
    }
  },

  // it will return new index for column what was moved, includes hidden columns
  adjustedIndex(visibleIndex) {
    let index = 0;
    let movesRemaining = visibleIndex + 1;

    while (movesRemaining > 0) {
      let column = this.collection.at(index);
      while (column && this.isColumnHidden(column)) {
        index++;
        column = this.collection.at(index);
      }
      index++;
      movesRemaining--;
    }

    return index - 1;
  },

  isColumnVisible(column) {
    if (column.has('condition')) {
      // can't use util.result as we are passing argument to the function
      if (util.isFunction(column.get('condition'))) {
        return column.get('condition')(column);
      }

      if (util.isBoolean(column.get('condition'))) {
        return column.get('condition');
      }

      return true;
    }
    return true;
  },

  isColumnHidden(column) {
    return !this.isColumnVisible(column);
  },

  onBeforeClose() {
    this.destroyReorder();
  },

  destroyReorder() {
    // destroy sortable element after view close
    if (this.draggable && this.$el.sortable('instance')) {
      this.$el.sortable('destroy');
    }
  }
});
